import {createApiClient, getOrganizationIdParamString, handleError, handleResponse} from '@/helpers/api'
import store from '@/store'
import {OrderStatus} from '@/enums'

const apiOrdersClient = createApiClient(window.config.VUE_APP_ORDER_API_BASE_URL)

// EXPORTS
export const ordersService = {
  getOrder,
  getOrders,
  createOrder,
  updateOrder,
  requestOrderCancellation,
  deleteOrder,
  getPrice,
  recalculatePrice,
  getPaymentUrl,
  setPaymentOpen,
  setAutoComplete,
  checkout,
  payment,
  getInvoice,
  getCalculatePrice,
  paymentWithCredit,
  getInvoices,
  customSort,
  exportCSV,
  clientCSVDownload,
  canCancelOrder,
  canDeleteOrder,
  canDownloadInvoice,
  canShowDetails,
  canEdit,
}

// API CALLS
async function getOrders(payload) {
  let query = ''
  // Date query extension
  query +=
    payload.filterRules && payload.filterRules.filterDate
      ? '&uploadDateTimeStart=' +
      payload.filterRules.dateFrom +
      ' 00:00:00' +
      '&uploadDateTimeEnd=' +
      payload.filterRules.dateTo +
      ' 23:59:59'
      : ''
  // DataSource query extension
  query +=
    payload.filterRules && payload.filterRules.filterDataSource
      ? '&dataSource=' + payload.filterRules.selectedDataSource
      : ''
  // DocumentStatus query extension
  query +=
    payload.filterRules && payload.filterRules.filterDocumentStatus
      ? '&documentValidationStatus=' +
      payload.filterRules.selectedDocumentStatus
      : ''
  // OrderStatus-Filter
  if (payload.filterRules && payload.filterRules.filterOrderStatus) {
    if (payload.filterRules.selectedOrderStatus.length === 1) {
      payload.min = Math.min(...payload.filterRules.selectedOrderStatus)
      payload.max = Math.max(...payload.filterRules.selectedOrderStatus)
    } else if (payload.filterRules.selectedOrderStatus.length > 1) {
      payload.filterRules.selectedOrderStatus.forEach(selectedOrderStatus => {
        query += '&orderStatusFilter=' + selectedOrderStatus + ''
      })
    }
  }

  return await apiOrdersClient
  .get(`/Order?orderStatusMin=${payload.min}&orderStatusMax=${payload.max}${query}`)
  .then(handleResponse)
  .catch(handleError)
}

async function getOrder(payload) {
  return await apiOrdersClient
  .get('/Order/' + payload)
  .then(handleResponse)
  .catch(handleError)
}

async function deleteOrder(payload) {
  return await apiOrdersClient
  .delete('/Order/' + payload)
  .then(handleResponse)
  .catch(handleError)
}

async function createOrder(payload) {
  return apiOrdersClient
  .post(`/Order${getOrganizationIdParamString()}`, payload)
  .then(handleResponse)
  .catch(handleError)
}

async function updateOrder(payload) {
  return apiOrdersClient
  .put('/Order/updateOrder', payload)
  .then(handleResponse)
  .catch(handleError)
}

function requestOrderCancellation(orderId) {
  return apiOrdersClient
  .post(`/Order/${orderId}/request-cancellation`)
  .then(handleResponse)
  .catch(handleError)
}

async function getPrice(payload) {
  return apiOrdersClient
  .get('/Order/' + payload + '/Price')
  .then(handleResponse)
  .catch(handleError)
}

async function recalculatePrice(payload) {
  return apiOrdersClient
  .post('/Order/recalculatePrice', payload)
  .then(handleResponse)
  .catch(handleError)
}

async function getPaymentUrl(payload) {
  return apiOrdersClient
  .post(`/Order/${payload.orderId}/Payment`,
    {returnUrl: payload.returnUrl})
  .then(handleResponse)
  .catch(handleError)
}

async function setPaymentOpen(payload) {
  return apiOrdersClient
  .put(`/Order/${payload}/SetPaymentOpen`)
  .then(handleResponse)
  .catch(handleError)
}

async function setAutoComplete(payload) {
  return apiOrdersClient
  .put('/Order/SetAutocomplete', payload)
  .then(handleResponse)
  .catch(handleError)
}

async function checkout(orderId) {
  return await apiOrdersClient
  .post(`/Order/${orderId}/Checkout`)
  .then(handleResponse)
  .catch(handleError)
}

async function payment(orderId, payload) {
  return await apiOrdersClient
  .post(`/Order/${orderId}/Payment`, payload)
  .then(handleResponse)
  .catch(handleError)
}

async function getInvoice(orderId) {
  return await apiOrdersClient
  .get(`/Invoice/${orderId}`)
  .then(handleResponse)
  .catch(handleError)
}

async function getCalculatePrice(payload) {
  return await apiOrdersClient
  .get(`/PriceCalculation?files=${payload.files}
    &pages=${payload.pages}
    &printMode=${payload.printMode}`)
  .then(handleResponse)
  .catch(handleError)
}

async function paymentWithCredit(orderId) {
  return await apiOrdersClient
  .post(`/Order/${orderId}/credit-account`)
  .then(handleResponse)
  .catch(handleError)
}

async function getInvoices() {
  return await apiOrdersClient
  .get('/invoice/all/')
  .then(handleResponse)
  .catch(handleError)
}

async function exportCSV(orderId, organizationId = null){
  const urlExtension = organizationId ? `/Order/${orderId}/download-csv-report?organizationId=${organizationId}` : `/Order/${orderId}/download-csv-report`
  return await apiOrdersClient
  .get(urlExtension)
  .then(handleResponse)
  .catch(handleError)
}

// HELPERS
async function clientCSVDownload(response, orderId) {
  const blob = new Blob([response], {type: 'text/csv;charset=utf-8;'})
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `order_${orderId}_report.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function customSort(items, index, isDesc) {
  items.sort((a, b) => {
    if (index[0] == 'orderNumber') {
      if (!isDesc[0]) {
        return a.orderId - b.orderId
      } else {
        return b.orderId - a.orderId
      }
    } else if (index[0] == 'orderCreateDateTime') {
      if (!isDesc[0]) {
        return (
          new Date(a.orderCreateDateTime) - new Date(b.orderCreateDateTime)
        )
      } else {
        return (
          new Date(b.orderCreateDateTime) - new Date(a.orderCreateDateTime)
        )
      }
    } else if (index[0] == 'orderPrice') {
      if (!isDesc[0]) {
        return a.orderPrice.totalNetPrice - b.orderPrice.totalNetPrice
      } else {
        return b.orderPrice.totalNetPrice - a.orderPrice.totalNetPrice
      }
    } else if (index[0] == 'dataSource') {
      if (!isDesc[0]) {
        return a.dataSource.localeCompare(b.dataSource)
      } else {
        return b.dataSource.localeCompare(a.dataSource)
      }
    } else if (index[0] == 'owner.lastName') {
      if (!isDesc[0]) {
        return a.owner && a.owner.lastName && b.owner && b.owner.lastName
          ? a.owner.lastName.localeCompare(b.owner.lastName)
          : false
      } else {
        return a.owner && a.owner.lastName && b.owner && b.owner.lastName
          ? b.owner.lastName.localeCompare(a.owner.lastName)
          : true
      }
    } else if (!isNaN(a[index[0]])) {
      if (!isDesc[0]) {
        return a[index[0]] - b[index[0]]
      } else {
        return b[index[0]] - a[index[0]]
      }
    } else {
      if (!isDesc[0]) {
        return a[index[0]] < b[index[0]] ? -1 : 1
      } else {
        return b[index[0]] < a[index[0]] ? -1 : 1
      }
    }
  })
  return items
}

// PERMISSIONS
function canCancelOrder(order) {
  // applies only for paid and partiallyCancelled orders
  // & skipPayment, as mentioned in #6089
  return !!(order.orderStatus === OrderStatus.paid ||
      order.orderStatus === OrderStatus.partiallyCancelled) &&
    store.getters.hasPermission('SkipPayment');
}

function canDeleteOrder(order) {
  switch (order.orderStatus) {
    case OrderStatus.open:
    case OrderStatus.pending:
    case OrderStatus.userActionRequired:
    case OrderStatus.validated:
    case OrderStatus.autocomplete:
    case OrderStatus.autocompletionFailed:
    case OrderStatus.validationError:
    case OrderStatus.paymentOpen:
    case OrderStatus.error:
    case OrderStatus.paymentPending:
    case OrderStatus.paymentRefused:
      return true
    default:
      return false
  }
}

function canShowDetails(order) {
  switch (order.orderStatus) {
    case OrderStatus.open:
    case OrderStatus.pending:
    case OrderStatus.userActionRequired:
    case OrderStatus.validated:
    case OrderStatus.autocomplete:
    case OrderStatus.autocompletionFailed:
    case OrderStatus.validationError:
    case OrderStatus.paymentOpen:
    case OrderStatus.paymentPending:
    case OrderStatus.paymentRefused:
    case OrderStatus.paid:
    case OrderStatus.inProduction:
    case OrderStatus.cancelled:
    case OrderStatus.finished:
      return true
    default:
      return false
  }
}

function canDownloadInvoice(order) {
  switch (order.orderStatus) {
    case OrderStatus.paid:
    case OrderStatus.inProduction:
    case OrderStatus.finished:
      return !store.getters.hasPermission('NoInvoiceDownload')
    default:
      return false
  }
}

function canEdit(order) {
  switch (order.orderStatus) {
    case OrderStatus.open:
    case OrderStatus.pending:
    case OrderStatus.userActionRequired:
    case OrderStatus.validated:
    case OrderStatus.autocomplete:
    case OrderStatus.autocompletionFailed:
    case OrderStatus.validationError:
    case OrderStatus.paymentOpen:
    case OrderStatus.paymentPending:
    case OrderStatus.paymentRefused:
      return true
    default:
      return false
  }
}
